// International Transfer account block (§6.3): country picker with blocked
// rows, IBAN-or-account switch, routing codes by country, SWIFT rules,
// currency, and the two address cards that open on error.
import { useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import type { CountryRef } from '@/types/phase2.types';
import { DEFAULT_ROUTING, ROUTING_CODES } from '@/mock/data';
import PickerModal from './PickerModal';
import type { BlockErrors, InternationalBlock } from './formModel';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'AED', 'SGD', 'AUD', 'CAD', 'JPY', 'CHF', 'SAR'];

interface InternationalFieldsProps {
  block: InternationalBlock;
  errors: BlockErrors;
  showErrors: boolean;
  countries: CountryRef[];
  onChange: (block: InternationalBlock) => void;
}

const InternationalFields = ({ block, errors, showErrors, countries, onChange }: InternationalFieldsProps) => {
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);

  const set = (patch: Partial<InternationalBlock>) => onChange({ ...block, ...patch });

  const pickCountry = (country: CountryRef) => {
    // Picking a country sets its default currency, clears the routing code and
    // switches to IBAN where IBAN is required (§6.3).
    set({
      countryCode: country.code,
      countryName: country.name,
      currency: country.currency,
      ibanRequired: country.ibanRequired,
      routingCode: '',
      idMode: country.ibanRequired ? 'IBAN' : '',
    });
    setCountryPickerOpen(false);
  };

  const routing = ROUTING_CODES[block.countryCode] ?? DEFAULT_ROUTING;
  const swiftOptional = ['US', 'GB', 'AU', 'CA'].includes(block.countryCode);
  const usesIban = block.ibanRequired || block.idMode === 'IBAN';
  const bankAddressError = showErrors && Boolean(errors.bankAddress);
  const accountAddressError = showErrors && Boolean(errors.accLine1 || errors.accTown || errors.accPostCode);

  return (
    <div className="nova-block-fields">
      <div className="mb-3">
        <label className="form-label">Country</label>
        <button
          type="button"
          className={`form-control text-start nova-picker-trigger ${showErrors && !block.countryCode ? 'is-invalid' : ''}`}
          onClick={() => setCountryPickerOpen(true)}
        >
          {block.countryName || '-- Select country --'}
        </button>
      </div>

      <div className="mb-3">
        <label className="form-label">Bank name</label>
        <input
          type="text"
          className={`form-control ${showErrors && errors.bankName ? 'is-invalid' : ''}`}
          value={block.bankName}
          onChange={(e) => set({ bankName: e.target.value })}
        />
      </div>

      <div className="mb-3">
        <label className="form-label">Account name</label>
        <input
          type="text"
          className={`form-control ${showErrors && errors.accountName ? 'is-invalid' : ''}`}
          value={block.accountName}
          onChange={(e) => set({ accountName: e.target.value })}
        />
        <div className="form-text">Exactly as the bank has it</div>
      </div>

      {block.countryCode && block.ibanRequired && (
        <p className="nova-info-box">IBAN is required for {block.countryName}.</p>
      )}

      {block.countryCode && !block.ibanRequired && (
        <div className="mb-3">
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name={`idmode-${block.key}`}
              id={`idmode-acc-${block.key}`}
              checked={block.idMode === 'ACCOUNT'}
              onChange={() => set({ idMode: 'ACCOUNT' })}
            />
            <label className="form-check-label" htmlFor={`idmode-acc-${block.key}`}>
              Account number
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name={`idmode-${block.key}`}
              id={`idmode-iban-${block.key}`}
              checked={block.idMode === 'IBAN'}
              onChange={() => set({ idMode: 'IBAN' })}
            />
            <label className="form-check-label" htmlFor={`idmode-iban-${block.key}`}>
              IBAN
            </label>
          </div>
          {showErrors && errors.idMode && <div className="invalid-feedback d-block">{errors.idMode}</div>}
        </div>
      )}

      {block.countryCode && usesIban && (
        <div className="mb-3">
          <label className="form-label">IBAN</label>
          <input
            type="text"
            className={`form-control ${showErrors && errors.iban ? 'is-invalid' : ''}`}
            value={block.iban}
            maxLength={34}
            onChange={(e) => set({ iban: e.target.value.replace(/\s/g, '').toUpperCase().slice(0, 34) })}
          />
          {showErrors && errors.iban && <div className="invalid-feedback d-block">{errors.iban}</div>}
        </div>
      )}

      {block.countryCode && !usesIban && block.idMode === 'ACCOUNT' && (
        <div className="mb-3">
          <label className="form-label">Account number</label>
          <input
            type="text"
            className={`form-control ${showErrors && errors.accountNumber ? 'is-invalid' : ''}`}
            value={block.accountNumber}
            maxLength={34}
            onChange={(e) => set({ accountNumber: e.target.value })}
          />
          {showErrors && errors.accountNumber && (
            <div className="invalid-feedback d-block">{errors.accountNumber}</div>
          )}
        </div>
      )}

      {block.countryCode && (
        <div className="mb-3">
          <label className="form-label">{routing.label}</label>
          <input
            type="text"
            className={`form-control ${showErrors && errors.routingCode ? 'is-invalid' : ''}`}
            value={block.routingCode}
            onChange={(e) => set({ routingCode: e.target.value })}
          />
          {showErrors && errors.routingCode && (
            <div className="invalid-feedback d-block">{errors.routingCode}</div>
          )}
        </div>
      )}

      <div className="mb-3">
        <label className="form-label">SWIFT/BIC{swiftOptional ? ' (optional)' : ''}</label>
        <input
          type="text"
          className={`form-control ${showErrors && errors.swift ? 'is-invalid' : ''}`}
          value={block.swift}
          maxLength={11}
          placeholder="HDFCINBBXXX"
          onChange={(e) => set({ swift: e.target.value.toUpperCase().slice(0, 11) })}
        />
        {showErrors && errors.swift && <div className="invalid-feedback d-block">{errors.swift}</div>}
      </div>

      <div className="mb-3">
        <label className="form-label">Account currency</label>
        <select className="form-select" value={block.currency} onChange={(e) => set({ currency: e.target.value })}>
          <option value="">-- Select currency --</option>
          {CURRENCIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <Accordion
        className="nova-address-accordion mb-3"
        defaultActiveKey={bankAddressError ? 'bank' : undefined}
        activeKey={undefined}
      >
        <Accordion.Item eventKey="bank">
          <Accordion.Header>
            Bank address {bankAddressError && <span className="nova-addr-error-dot" />}
          </Accordion.Header>
          <Accordion.Body>
            {bankAddressError && <div className="invalid-feedback d-block mb-2">{errors.bankAddress}</div>}
            <div className="mb-2">
              <label className="form-label">Bank address line 1</label>
              <input type="text" className="form-control" value={block.bankLine1} onChange={(e) => set({ bankLine1: e.target.value })} />
            </div>
            <div className="mb-2">
              <label className="form-label">Bank address line 2</label>
              <input type="text" className="form-control" value={block.bankLine2} onChange={(e) => set({ bankLine2: e.target.value })} />
            </div>
            <div className="row">
              <div className="col-md-6 mb-2">
                <label className="form-label">Bank post code</label>
                <input type="text" className="form-control" value={block.bankPostCode} onChange={(e) => set({ bankPostCode: e.target.value })} />
              </div>
              <div className="col-md-6 mb-2">
                <label className="form-label">Bank town</label>
                <input type="text" className="form-control" value={block.bankTown} onChange={(e) => set({ bankTown: e.target.value })} />
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="account">
          <Accordion.Header>
            Account address {accountAddressError && <span className="nova-addr-error-dot" />}
          </Accordion.Header>
          <Accordion.Body>
            <div className="mb-2">
              <label className="form-label">Account address line 1</label>
              <input
                type="text"
                className={`form-control ${showErrors && errors.accLine1 ? 'is-invalid' : ''}`}
                value={block.accLine1}
                onChange={(e) => set({ accLine1: e.target.value })}
              />
              {showErrors && errors.accLine1 && <div className="invalid-feedback d-block">{errors.accLine1}</div>}
            </div>
            <div className="mb-2">
              <label className="form-label">Account address line 2</label>
              <input type="text" className="form-control" value={block.accLine2} onChange={(e) => set({ accLine2: e.target.value })} />
            </div>
            <div className="row">
              <div className="col-md-6 mb-2">
                <label className="form-label">Account post code</label>
                <input type="text" className="form-control" value={block.accPostCode} onChange={(e) => set({ accPostCode: e.target.value })} />
              </div>
              <div className="col-md-6 mb-2">
                <label className="form-label">Account town</label>
                <input
                  type="text"
                  className={`form-control ${showErrors && errors.accTown ? 'is-invalid' : ''}`}
                  value={block.accTown}
                  onChange={(e) => set({ accTown: e.target.value })}
                />
                {showErrors && errors.accTown && <div className="invalid-feedback d-block">{errors.accTown}</div>}
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <p className="nova-block-note">
        International transfers follow the Liberalised Remittance Scheme (up to USD 2,50,000 a year).
        You&apos;ll choose the purpose when you send money.
      </p>

      <PickerModal
        show={countryPickerOpen}
        title="Select country"
        searchHint="Search country"
        rows={countries.map((c) => ({
          key: c.code,
          title: c.name,
          subtitle: c.currency,
          disabled: c.blocked,
          disabledNote: c.blocked ? "Transfers to this country aren't allowed." : undefined,
        }))}
        emptyText="No matches"
        onSelect={(row) => {
          const country = countries.find((c) => c.code === row.key);
          if (country) pickCountry(country);
        }}
        onClose={() => setCountryPickerOpen(false)}
      />
    </div>
  );
};

export default InternationalFields;
